import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Grid, List } from '../components/icons';
import ProductCard from '../components/ProductCard';
import { Product, Category } from '@dentopharma/types';
import { api } from '../lib/api';
import clinicImage from '../assets/clinic .jpg';

const Shop = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Advanced filters
  const [manufacturer] = useState<string>('all');

  // Fetch products from API
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);
  
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/products');
      
      if (response.data.success) {
        setProducts(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products. Please try again later.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Don't set error for categories, just use empty array
      setCategories([]);
    }
  };

  // Filter and sort products
  const filteredProducts = products
    .filter(product => {
      // Search filter
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Category filter
      const matchesCategory = selectedCategory === 'all' || product.categoryId === selectedCategory;

      // Manufacturer filter
      const matchesManufacturer = manufacturer === 'all' || product.manufacturerId === manufacturer;

      return matchesSearch && matchesCategory && matchesManufacturer;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        default:
          return 0;
      }
    });

  // helper removed as unused

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D56A6] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dental supplies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchProducts}
            className="bg-[#0D56A6] text-white px-6 py-2 rounded-lg hover:bg-[#0A4A8F] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section data-hero className="relative overflow-hidden bg-[#062f63] text-white pt-28 pb-20 md:pt-32 md:pb-24">
        {/* Background clinic image */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${clinicImage})` }}
        />
        {/* Gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D56A6]/85 via-[#0a4690]/75 to-[#062f63]/90" />

        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {/* Heading */}
            <h1 className="text-4xl md:text-6xl font-extrabold mb-5 leading-tight text-white">
              The right material
              <span className="block text-[#8EC641]">for every case</span>
            </h1>

            {/* Subtext */}
            <p className="text-lg md:text-xl text-blue-100/90 max-w-2xl mx-auto mb-8">
              Explore the materials Dentopharma supplies — what each one is for, when to use it, and how it performs. Backed by Ivoclar. Talk to us when you’re ready.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
              <a
                href="#products"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-[#8EC641] text-white font-semibold shadow-lg shadow-[#8EC641]/30 hover:bg-[#7ab535] hover:-translate-y-0.5 transition-all"
              >
                Explore the range
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              </a>
              <a
                href="/verify"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold hover:bg-white/20 transition-all"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Verify a Product
              </a>
            </div>

            {/* Trust signals — honest, no inflated numbers */}
            <div className="flex flex-wrap justify-center gap-2.5">
              {['Authorised Ivoclar distributor', '100% genuine products', 'Delivered across Tanzania'].map(t => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3.5 py-1.5 text-xs md:text-sm text-white/90">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8EC641]" />{t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0 leading-none">
          <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none" className="w-full h-12 md:h-20">
            <path d="M0 80V40C240 10 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="#F9FAFB" />
          </svg>
        </div>
      </section>

      {/* Search and Filters Section */}
      <section className="py-6 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch lg:items-center justify-between">
            {/* Search Bar */}
            <div className="w-full lg:flex-1 lg:max-w-md">
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D56A6]/40 focus:border-transparent outline-none"
                />
              </div>
            </div>

            {/* Sort / View controls */}
            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 min-w-0 lg:flex-none px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D56A6]/40 focus:border-transparent outline-none"
              >
                <option value="name">Name A-Z</option>
                <option value="newest">Newest First</option>
              </select>

              {/* View Mode Toggle */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden flex-shrink-0">
                <button
                  aria-label="Grid view"
                  onClick={() => setViewMode('grid')}
                  className={`p-3 ${viewMode === 'grid' ? 'bg-[#0D56A6] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <Grid size={20} />
                </button>
                <button
                  aria-label="List view"
                  onClick={() => setViewMode('list')}
                  className={`p-3 ${viewMode === 'list' ? 'bg-[#0D56A6] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <List size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Category pills — browse by family (Ivoclar-style) */}
          {categories.length > 0 && (
            <div className="mt-5 -mx-1 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${selectedCategory === 'all' ? 'bg-[#0D56A6] text-white border-[#0D56A6]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#0D56A6]/50 hover:text-[#0D56A6]'}`}
              >
                All products
              </button>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${selectedCategory === category.id ? 'bg-[#0D56A6] text-white border-[#0D56A6]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#0D56A6]/50 hover:text-[#0D56A6]'}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-12 scroll-mt-24">
        <div className="container mx-auto px-4">
          {/* Results Summary */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Catalogue</h2>
              <p className="text-gray-600">
                {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Products Grid/List */}
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <div className="flex justify-center text-gray-300 mb-4"><Search size={56} /></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                className="bg-[#0D56A6] text-white px-6 py-2 rounded-lg hover:bg-[#0A4A8F] transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 
              'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 
              'space-y-4'
            }>
              <AnimatePresence>
                {filteredProducts.map((product) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                                         <ProductCard
                       product={product}
                     />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Shop;
